import { useFloating, flip, hide, offset, shift, autoUpdate } from '@floating-ui/react'
import type { CSSProperties } from 'react'
import { useMemo } from 'react'

import type { IPlacement, IType } from './types'

const TOOLTIP_MAX_WIDTH = 300
const OFFSET = 4
const isBottom = (placement: IPlacement) => placement.toLowerCase().startsWith('bottom')
const isTop = (placement: IPlacement) => placement.toLowerCase().startsWith('top')
const isLeft = (placement: IPlacement) => placement.toLowerCase().startsWith('left')
const isRight = (placement: IPlacement) => placement.toLowerCase().startsWith('right')

interface IPlacementType {
  targetEle: HTMLDivElement | null
  popupEle: HTMLDivElement | null
  placement: IPlacement
  type: IType
  offset: [number, number]
  triggerOpen: (open: boolean) => void
  open: boolean
}

const DEFAULT_POSITIONS = {
  visibility: 'hidden',
  width: 'auto',
  maxWidth: '100%',
  minWidth: 'auto',
} as const

export const useGetPosition = ({
  targetEle,
  popupEle,
  placement,
  type,
  offset: offsetParams,
  triggerOpen,
  open,
}: IPlacementType): CSSProperties => {
  const [y, x] = offsetParams
  const { width: targetEleWidth } = targetEle?.getBoundingClientRect() || {}

  const isCrossAxis = isTop(placement) || isLeft(placement)
  const isMainAxis = isRight(placement) || isBottom(placement)

  const {
    x: left,
    y: top,
    isPositioned,
  } = useFloating({
    strategy: 'absolute',
    elements: { reference: targetEle, floating: popupEle },
    placement,
    open,
    onOpenChange: triggerOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({
        mainAxis: y ?? (isMainAxis ? OFFSET : 0),
        crossAxis: x ?? (isCrossAxis ? OFFSET : 0),
      }),
      flip(),
      shift({ padding: OFFSET }),
      hide(),
    ],
  })

  return useMemo(() => {
    if (!isPositioned) {
      return DEFAULT_POSITIONS
    }

    return {
      ...DEFAULT_POSITIONS,
      ...(type === 'tooltip' && { maxWidth: TOOLTIP_MAX_WIDTH }),
      ...(type === 'dropdown' && { minWidth: targetEleWidth, width: targetEleWidth }),
      ...(type === 'popover' && { minWidth: targetEleWidth }),
      visibility: 'visible',
      top,
      left,
    }
  }, [isPositioned, type, targetEleWidth, top, left])
}
